import { FC, useMemo, useState } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Button, Skeleton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { ScalarLineChart } from 'components/Charts';
import {
  ChangeLabel,
  CurrentValue,
  HeaderLeft,
  MarksTable,
  MarksTableCell,
  MarksTableHead,
  MarksTableRow,
  SeriesChip,
  SeriesToggleRow,
  ViewToggle,
} from './ZanbatoReportedMarksCard.styles';
import {
  buildChartSeries,
  buildValuationFundOverlaySeries,
  computeChange,
  filterMarksByPeriod,
  formatCurrency,
  formatPeriodLabel,
  getLatestWeightedAvg,
  getPeriodStartDate,
  hasAnyStat,
  Period,
  PERIODS,
  PeriodValue,
  SERIES_CONFIGS,
  toNum,
  ZanbatoMarksValuationItem,
  ZanbatoMarksValuationResponse,
  ZanbatoReportedMarkItem,
  ZanbatoReportedMarksResponse,
} from './ZanbatoReportedMarksCard.utils';
import { CardHeaderRow, FundFilterRow, FundPill } from '../MutualFundMarksCard/MutualFundMarksCard.styles';
import {
  EndDateMode,
  EndDateModeValue,
  formatChartDate,
  ViewMode,
  ViewModeValue,
} from '../MutualFundMarksCard/MutualFundMarksCard.utils';

interface Props {
  data: ZanbatoReportedMarksResponse | undefined;
  isLoading: boolean;
  valuationData: ZanbatoMarksValuationResponse | undefined;
  isValuationLoading: boolean;
  measurementDate?: string;
  endDateMode: EndDateModeValue;
  view: ViewModeValue;
  onViewChange: (v: ViewModeValue) => void;
  period: PeriodValue;
  onPeriodChange: (v: PeriodValue) => void;
  enabledIds: Set<string>;
  onEnabledIdsChange: (ids: Set<string>) => void;
}

const ZanbatoMarksBody: FC<Props> = ({
  data,
  isLoading,
  valuationData,
  isValuationLoading,
  measurementDate,
  endDateMode,
  view,
  onViewChange,
  period,
  onPeriodChange,
  enabledIds,
  onEnabledIdsChange,
}) => {
  const marks = useMemo<ZanbatoReportedMarkItem[]>(() => data?.marks ?? [], [data?.marks]);
  const valuations = useMemo<ZanbatoMarksValuationItem[]>(() => valuationData?.valuations ?? [], [valuationData?.valuations]);

  const nameOptions = useMemo<string[]>(() => {
    const names = valuations.map(v => v.name).filter((n): n is string => !!n);
    return [...new Set(names)].sort();
  }, [valuations]);

  const [selectedFunds, setSelectedFunds] = useState<Set<string>>(new Set());

  // Ignore placeholder rows (report_period set, every stat null) — Zanbato returns
  // these for recent months whose filings haven't landed yet.
  const latestDataDate = useMemo(() => {
    const dated = marks.filter(m => m.report_period && hasAnyStat(m));
    if (!dated.length) return undefined;
    return [...dated].sort((a, b) => b.report_period!.localeCompare(a.report_period!))[0].report_period ?? undefined;
  }, [marks]);

  const effectiveEndDate = useMemo(
    () => (endDateMode === EndDateMode.Measurement && measurementDate ? measurementDate : latestDataDate),
    [endDateMode, measurementDate, latestDataDate],
  );

  // Valuation data uses its own latest date as the upper bound so marks endpoint
  // lag doesn't cut off more recent valuation records.
  const latestValuationDate = useMemo(() => {
    const dated = valuations.filter(v => v.date);
    if (!dated.length) return undefined;
    return [...dated].sort((a, b) => b.date!.localeCompare(a.date!))[0].date ?? undefined;
  }, [valuations]);

  const effectiveValuationEndDate = useMemo(
    () => (endDateMode === EndDateMode.Measurement && measurementDate ? measurementDate : latestValuationDate),
    [endDateMode, measurementDate, latestValuationDate],
  );

  const filteredValuations = useMemo(() => {
    let filtered = valuations.filter(v => v.date);
    if (effectiveValuationEndDate) {
      filtered = filtered.filter(v => v.date! <= effectiveValuationEndDate);
    }
    if (period !== Period.All && effectiveValuationEndDate) {
      const startDate = getPeriodStartDate(effectiveValuationEndDate, period);
      if (startDate) {
        filtered = filtered.filter(v => v.date! >= startDate);
      }
    }
    return filtered;
  }, [valuations, period, effectiveValuationEndDate]);

  const filteredMarks = useMemo(
    () => filterMarksByPeriod(marks, period, effectiveEndDate),
    [marks, period, effectiveEndDate],
  );

  const series = useMemo(() => buildChartSeries(filteredMarks, enabledIds), [filteredMarks, enabledIds]);
  const valuationOverlaySeries = useMemo(
    () => buildValuationFundOverlaySeries(filteredValuations, [...selectedFunds]),
    [filteredValuations, selectedFunds],
  );
  const tableValuations = useMemo(
    () => selectedFunds.size === 0 ? filteredValuations : filteredValuations.filter(v => selectedFunds.has(v.name ?? '')),
    [filteredValuations, selectedFunds],
  );
  const { value: lastValuationAvg, period: lastValuationAvgPeriod } = useMemo(
    () => getLatestWeightedAvg(marks, effectiveEndDate),
    [marks, effectiveEndDate],
  );
  const change = useMemo(() => computeChange(filteredMarks), [filteredMarks]);

  const hasData = marks.length > 0;

  const toggleSeries = (id: string) => {
    const next = new Set(enabledIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onEnabledIdsChange(next);
  };

  return (
    <>
      <CardHeaderRow>
        <HeaderLeft>
          {lastValuationAvg !== null && (
            <>
              <CurrentValue>{formatCurrency(lastValuationAvg)}</CurrentValue>
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                weighted avg / share{lastValuationAvgPeriod ? ` · ${formatPeriodLabel(lastValuationAvgPeriod)}` : ''}
              </Typography>
              {change && (
                <ChangeLabel positive={change.abs >= 0}>
                  {change.abs >= 0 ? '+' : ''}{change.abs.toFixed(2)} ({change.abs >= 0 ? '+' : ''}{change.pct.toFixed(2)}%)
                </ChangeLabel>
              )}
              {latestDataDate && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontStyle: 'italic' }}>
                  Data through {formatPeriodLabel(latestDataDate)}
                </Typography>
              )}
            </>
          )}
        </HeaderLeft>
        <ViewToggle>
          {hasData && (
            <ToggleButtonGroup
              value={period}
              exclusive
              size="small"
              onChange={(_e, v: PeriodValue | null) => {
                if (v) {
                  onPeriodChange(v);
                }
              }}
            >
              {PERIODS.map(p => (
                <ToggleButton key={p} value={p}>{p}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(_e, v: ViewModeValue | null) => {
              if (v) {
                onViewChange(v);
              }
            }}
          >
            <ToggleButton value={ViewMode.Chart}>Chart</ToggleButton>
            <ToggleButton value={ViewMode.Table}>Table</ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: '0.7rem !important' }} />}
            href="https://zanbato.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', height: 26, py: 0 }}
          >
            Zanbato
          </Button>
        </ViewToggle>
      </CardHeaderRow>

      {view === ViewMode.Chart && (
        <>
          {(isLoading || isValuationLoading) && (
            <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
          )}
          {!isLoading && !isValuationLoading && !hasData && valuations.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
              No Zanbato data available for this company.
            </Typography>
          )}
          {!isLoading && !isValuationLoading && (hasData || valuations.length > 0) && (
            <>
              {hasData && (
                <SeriesToggleRow>
                  {SERIES_CONFIGS.map(s => (
                    <SeriesChip
                      key={s.id}
                      label={s.label}
                      size="small"
                      variant="outlined"
                      active={enabledIds.has(s.id)}
                      seriesColor={s.color}
                      onClick={() => toggleSeries(s.id)}
                    />
                  ))}
                </SeriesToggleRow>
              )}
              {nameOptions.length > 1 && (
                <FundFilterRow>
                  {nameOptions.map(name => (
                    <FundPill
                      key={name}
                      label={name}
                      size="small"
                      variant="outlined"
                      active={selectedFunds.has(name)}
                      onClick={() => {
                        const next = new Set(selectedFunds);
                        if (next.has(name)) next.delete(name); else next.add(name);
                        setSelectedFunds(next);
                      }}
                    />
                  ))}
                </FundFilterRow>
              )}
              <ScalarLineChart
                series={[...series, ...valuationOverlaySeries]}
                notMerge
                smooth
                showPoints
                style={{ height: 280 }}
                config={{
                  legend: { show: true, position: 'top' },
                  xAxis: { formatter: v => formatPeriodLabel(String(v)) },
                  yAxis: { formatter: v => `$${v}` },
                  options: {
                    tooltip: {
                      trigger: 'axis',
                      appendToBody: true,
                      formatter: (params: any) => {
                        if (!Array.isArray(params) || !params.length) return '';
                        const dateLabel = formatChartDate(String(params[0].axisValue));
                        return (
                          `<div style="font-weight:700;margin-bottom:4px;font-size:0.82rem">${dateLabel}</div>${params
                            .filter((p: any) => p.value != null)
                            .map((p: any) => `<div>${p.marker}${p.seriesName}: <b>$${Number(p.value).toFixed(2)}</b></div>`)
                            .join('')}`
                        );
                      },
                    },
                  },
                }}
              />
            </>
          )}
        </>
      )}

      {view === ViewMode.Table && (
        <>
          {isValuationLoading && (
            <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1, mt: 1 }} />
          )}
          {!isValuationLoading && (
            <>
              {tableValuations.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
                  No valuation data available.
                </Typography>
              ) : (
                <MarksTable>
                  <thead>
                    <MarksTableRow>
                      <MarksTableHead>Name</MarksTableHead>
                      <MarksTableHead>Date</MarksTableHead>
                      <MarksTableHead>Security</MarksTableHead>
                      <MarksTableHead align="right">Total Value</MarksTableHead>
                      <MarksTableHead align="right">Price Per Share</MarksTableHead>
                    </MarksTableRow>
                  </thead>
                  <tbody>
                    {[...tableValuations]
                      .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
                      .map((v, i) => (
                        <MarksTableRow key={`${v.name}-${v.date}-${i}`}>
                          <MarksTableCell>{v.name ?? '—'}</MarksTableCell>
                          <MarksTableCell>{v.date ?? '—'}</MarksTableCell>
                          <MarksTableCell>{v.security ?? '—'}</MarksTableCell>
                          <MarksTableCell align="right">{formatCurrency(toNum(v.total_value))}</MarksTableCell>
                          <MarksTableCell align="right">{formatCurrency(toNum(v.price_per_share))}</MarksTableCell>
                        </MarksTableRow>
                      ))}
                  </tbody>
                </MarksTable>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export { ZanbatoMarksBody };
